import { SdxAdminSubject } from '@psw/shared';
import { useCaslAbility } from '@sodexo/admin-module-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { parseUrlQuery, UrlKeys } from '../Components/Utils/UrlUtil';
import i18n from '../i18n';
import { Catalog } from '../Models/Catalog';
import { useCatalogs } from '../Queries/catalogs.queries';
import { useStore } from '../store/store';

export function useInitApp() {
  const [catalog, setCatalog] = useStore(useShallow(state => [state.catalog, state.setCatalog]));
  const location = useLocation();
  const [isInit, setIsInit] = useState<{ done: boolean; forbidden: boolean }>({ done: false, forbidden: false });
  const [enableFetchCatalogs, setEnableFetchCatalogs] = useState(false);
  const catalogs = useCatalogs({ enabled: enableFetchCatalogs });
  const ability = useCaslAbility();

  useEffect(() => {
    if (ability.loading) return;

    const urlQuery = parseUrlQuery(location);
    const organizationFromUrl = urlQuery[UrlKeys.Organization] as string;
    const catalogFromUrl = urlQuery[UrlKeys.Catalog] as string;
    const taxonomyFromUrl = urlQuery[UrlKeys.Taxonomy] as string;

    if (
      catalog &&
      organizationFromUrl === catalog.organization &&
      catalogFromUrl === catalog.name &&
      taxonomyFromUrl === catalog.taxonomy
    ) {
      // Catalog in local storage match the one in the url, we don't need to fetch the catalogs
      setIsInit({ done: true, forbidden: ability.cannot('read', SdxAdminSubject.Catalog) });
      i18n.changeLanguage(catalog.language);
    } else {
      setEnableFetchCatalogs(true);
    }
  }, [ability]);

  useEffect(() => {
    if (catalogs.data) {
      if (catalogs.data.length === 0) {
        setIsInit({ done: true, forbidden: true });
        return;
      }

      const urlQuery = parseUrlQuery(location);
      const organizationFromUrl = urlQuery[UrlKeys.Organization] as string;
      const catalogFromUrl = urlQuery[UrlKeys.Catalog] as string;
      const taxonomyFromUrl = urlQuery[UrlKeys.Taxonomy] as string;

      const organizationToUse =
        catalogs.data.find(r =>
          organizationFromUrl ? r.organization === organizationFromUrl : r.organization === catalog?.organization,
        ) || catalogs.data[0];
      const catalogToUse =
        organizationToUse.catalogs.find(c => (catalogFromUrl ? c.name === catalogFromUrl : c.name === catalog?.name)) ||
        organizationToUse.catalogs[0];
      const taxonomyToUse =
        catalogToUse.taxonomies.find(t => (taxonomyFromUrl ? t === taxonomyFromUrl : t === catalog?.taxonomy)) ||
        catalogToUse.taxonomies[0];

      const defaultCatalog = new Catalog({
        ...catalogToUse,
        organization: organizationToUse.organization,
        taxonomy: taxonomyToUse,
      });
      setCatalog(defaultCatalog);
      i18n.changeLanguage(catalog?.language);
      setIsInit({ done: true, forbidden: false });
    }
  }, [catalogs.data]);

  useEffect(() => {
    if (catalogs.isError && catalogs.isForbiddenByAdminModule) {
      setIsInit({ done: true, forbidden: true });
    }
  }, [catalogs.isError, catalogs.isForbiddenByAdminModule]);

  return isInit;
}
